/**
 * Good Atoms Landing Page — Redesigned
 *
 * Philosophy:
 *  - Bright cream background throughout (matches app interior — no more dark→light shock)
 *  - Characters front and center: mascots are the visual heroes
 *  - TikTok-era motion: spring physics, continuous floats, scroll-triggered stagger
 *  - Bold color sections that pop but never go dark on the main path
 */

import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import VariableProximity from '../components/ui/VariableProximity';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { LanguageToggle } from '../components/ui/LanguageToggle';
import { Mascot, MASCOT_NAMES } from '../components/mascots';
import type { MascotEmotion, MascotSubject } from '../components/mascots';
import { useT } from '../i18n';
import { SEOHead } from '../components/blog/SEOHead';
import { BlogCard } from '../components/blog/BlogCard';
import { getRecentPosts } from '../utils/blogLoader';
import { FounderCard } from '../components/landing/FounderCard';
import { LessonPreviewIllustration } from '../components/landing/LessonPreviewIllustration';
import { useLandingMotion } from '../components/landing/useLandingMotion';
import { captureUtm } from '../utils/utm';
import { trackEvent } from '../lib/analytics';

/** Track a signup-intent CTA click, then navigate. Used by every primary conversion button. */
function makeCtaHandler(navigate: (p: string) => void, cta: string, location: string) {
  return () => {
    trackEvent('cta_click', { cta, location });
    navigate(SIGNUP_PATH);
  };
}

/** Canonical signup path — every conversion CTA on Landing must use this. */
const SIGNUP_PATH = '/login?signup=1';

/** Returns true when the viewport is narrower than 640px (sm breakpoint). */
function useIsMobile(breakpoint = 640) {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [breakpoint],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches,
    () => false,
  );
}

// ─── Spring presets ──────────────────────────────────────────────────────────

const spring = { type: 'spring' as const, stiffness: 280, damping: 22 };
const springLazy = { type: 'spring' as const, stiffness: 180, damping: 20 };

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show:   { opacity: 1, y: 0, transition: spring },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const popIn = {
  hidden: { opacity: 0, scale: 0.7 },
  show:   { opacity: 1, scale: 1, transition: { type: 'spring' as const, stiffness: 340, damping: 20 } },
};

// ─── Data ────────────────────────────────────────────────────────────────────

const SUBJECTS: { subject: MascotSubject; label: string; labelEn: string; color: string }[] = [
  { subject: 'mathematics', label: 'Matematikk', labelEn: 'Mathematics', color: '#F59E0B' },
  { subject: 'science',     label: 'Naturfag',   labelEn: 'Science',     color: '#16A34A' },
  { subject: 'technology',  label: 'Teknologi',  labelEn: 'Technology',  color: '#2563EB' },
  { subject: 'engineering', label: 'Ingeniørfag',labelEn: 'Engineering', color: '#EF4444' },
  { subject: 'arts',        label: 'Kunst',      labelEn: 'Arts',        color: '#9333EA' },
  { subject: 'nature',      label: 'Natur',      labelEn: 'Nature',      color: '#13AA0D' },
];

// ─── Credibility artifacts ────────────────────────────────────────────────────
//
// What we ship to parents in place of fabricated testimonials. Three concrete
// proofs of curriculum alignment + a live preview of what a Good Atoms lesson
// actually looks like. Sources are pulled from real LK20 goals in
// content/blog/no/*.md frontmatter — not invented.

const LK20_ALIGNMENT: { titleNo: string; titleEn: string; goalNo: string; goalEn: string; gradeNo: string; gradeEn: string }[] = [
  {
    titleNo: 'Hva er en regnbue?',
    titleEn: 'What makes a rainbow?',
    goalNo: 'Utforske og beskrive ulike fenomener knyttet til lys og syn',
    goalEn: 'Explore and describe phenomena connected to light and sight',
    gradeNo: 'Naturfag · 2.–4. trinn',
    gradeEn: 'Science · grades 2–4',
  },
  {
    titleNo: 'Hva er fotosyntese?',
    titleEn: 'What is photosynthesis?',
    goalNo: 'Utforske og beskrive mangfoldet av livsformer og sammenhenger i naturen',
    goalEn: 'Explore and describe the diversity of life forms and connections in nature',
    gradeNo: 'Naturfag · livet på jorda',
    gradeEn: 'Science · life on Earth',
  },
  {
    titleNo: 'Hvorfor trenger vi biene?',
    titleEn: 'Why do we need the bees?',
    goalNo: 'Planlegge og gjennomføre enkle feltarbeid og undersøkelser av organismer i nærmiljøet',
    goalEn: 'Plan and carry out simple field work and investigations of organisms in the local environment',
    gradeNo: 'Naturfag · 5.–7. trinn',
    gradeEn: 'Science · grades 5–7',
  },
];

const PEDAGOGICAL_SOURCES = ['LK20', 'Kunnskapsløftet', 'CS Unplugged', 'Code.org', 'Scratch Foundation', 'MIT OpenCourseWare'];

// ─── Demo lesson snippets ─────────────────────────────────────────────────────

type DemoSubject = 'mathematics' | 'science' | 'technology';

const DEMO_COLORS: Record<DemoSubject, {
  pill: string; card: string; border: string; num: string;
}> = {
  mathematics: {
    pill:   'bg-[#FEF3C7] text-[#D97706] border-[#FDE68A]',
    card:   'from-[#FFFBEB] to-[#FEF3C7]/50',
    border: 'border-[#FDE68A]',
    num:    'text-[#D97706]',
  },
  science: {
    pill:   'bg-[#DCFCE7] text-[#16A34A] border-[#BBF7D0]',
    card:   'from-[#F0FDF4] to-[#DCFCE7]/50',
    border: 'border-[#BBF7D0]',
    num:    'text-[#16A34A]',
  },
  technology: {
    pill:   'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE]',
    card:   'from-[#EFF6FF] to-[#DBEAFE]/50',
    border: 'border-[#BFDBFE]',
    num:    'text-[#2563EB]',
  },
};

const DEMO_LESSONS: Array<{
  subject: DemoSubject;
  duration: number;
  tabNo: string; tabEn: string;
  hook: { no: string; en: string };
  hookBody: { no: string; en: string };
  bridge: { no: string; en: string };
}> = [
  {
    subject: 'mathematics',
    duration: 30,
    tabNo: 'Matematikk', tabEn: 'Mathematics',
    hook: {
      no: 'Hva skjer når vi gir ukjente tall et navn?',
      en: 'What happens when we give unknown numbers a name?',
    },
    hookBody: {
      no: 'Tenk deg at du kjøper et antall boller. Du vet ikke nøyaktig hvor mange — kanskje 3, kanskje 7, kanskje 20. Men du vet at hver bolle koster 12 kroner. Hvordan skriver du ned totalprisen uten å kjenne antallet?',
      en: "You're buying buns. You don't know exactly how many — maybe 3, maybe 7, maybe 20. But you know each bun costs 12 kroner. How do you write down the total price without knowing the number?",
    },
    bridge: {
      no: 'Varukurven din, telefonminnet ditt, og matoppskriften din — de bruker alle «ukjente tall» daglig.',
      en: 'Your shopping basket, phone storage, and recipes — they all use "unknown numbers" every day.',
    },
  },
  {
    subject: 'science',
    duration: 25,
    tabNo: 'Naturfag', tabEn: 'Science',
    hook: {
      no: 'Hvorfor kan du se din egen åndedrett på en kald dag?',
      en: 'Why can you see your own breath on a cold day?',
    },
    hookBody: {
      no: 'Når du puster ut på en kald vinterdag, danner det seg en liten sky foran deg. Det ser ut som magi — men det er faktisk noe som skjer millioner av ganger inne i kroppen din hvert minutt.',
      en: "When you breathe out on a cold winter's day, a tiny cloud forms in front of you. It looks like magic — but it's actually something that happens millions of times inside your body every minute.",
    },
    bridge: {
      no: 'Det samme skjer inne i skyene over deg, i kjøleskapet ditt, og i svømmehallen.',
      en: 'The same thing happens inside the clouds above you, in your refrigerator, and at the swimming pool.',
    },
  },
  {
    subject: 'technology',
    duration: 20,
    tabNo: 'Teknologi', tabEn: 'Technology',
    hook: {
      no: 'Hvordan kan en maskin gjenkjenne ansiktet ditt på under ett sekund?',
      en: 'How can a machine recognise your face in under one second?',
    },
    hookBody: {
      no: 'Telefonen din gjenkjenner ansiktet ditt på under ett sekund. Den har aldri fått en «oppskrift» på hva et ansikt er. Den har aldri sett deg før heller. Så hvordan vet den at det er deg?',
      en: "Your phone recognises your face in under a second. It was never given a recipe for what a face is. It had never seen you before either. So how does it know it's you?",
    },
    bridge: {
      no: 'Den samme teknologien brukes av legen din, flyplassen, og i sosiale medier du bruker.',
      en: 'The same technology is used by your doctor, the airport, and the social media you use.',
    },
  },
];

// ─── Floating blob decoration ─────────────────────────────────────────────────

function FloatBlob({
  size, color, x, y, delay, duration,
}: {
  size: number; color: string; x: string; y: string; delay: number; duration: number;
}) {
  return (
    <motion.div
      aria-hidden="true"
      animate={{ y: [0, -22, 0], scale: [1, 1.04, 1] }}
      transition={{ duration, repeat: Infinity, ease: 'easeInOut', delay }}
      style={{
        position: 'absolute',
        left: x, top: y,
        width: size, height: size,
        borderRadius: '50%',
        background: color,
        opacity: 0.35,
        filter: 'blur(2px)',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}


// ─── Live Demo section ────────────────────────────────────────────────────────

function LessonDemoSection({ lang, t, onSignup }: {
  lang: string;
  t: (key: string) => string;
  onSignup: () => void;
}) {
  const [active, setActive] = useState(0);
  const lesson = DEMO_LESSONS[active];
  const colors = DEMO_COLORS[lesson.subject];

  const movements = [
    { num: '01', label: lang === 'en' ? 'Wonder'     : 'Undring',  locked: false },
    { num: '02', label: lang === 'en' ? 'Foundation' : 'Grunnlag', locked: true  },
    { num: '03', label: lang === 'en' ? 'Build'      : 'Bygg',     locked: true  },
    { num: '04', label: lang === 'en' ? 'Practice'   : 'Øvelse',   locked: true  },
  ];

  return (
    <section id="demo" className="py-16 sm:py-24 px-4 sm:px-6 bg-page">
      <div className="max-w-3xl mx-auto space-y-10">

        {/* Section header */}
        <motion.div
          className="text-center space-y-3"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={spring}
        >
          <p className="text-xs font-extrabold text-teal uppercase tracking-[0.25em]">
            {t('demo.tag')}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-ink">
            {t('demo.heading')}
          </h2>
          <p className="text-ink/50 text-base sm:text-lg font-medium max-w-xl mx-auto px-2 sm:px-0">
            {t('demo.sub')}
          </p>
        </motion.div>

        {/* Subject selector tabs */}
        <motion.div
          className="flex justify-center gap-3 flex-wrap"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ ...spring, delay: 0.1 }}
        >
          {DEMO_LESSONS.map((l, i) => {
            const c = DEMO_COLORS[l.subject];
            const isActive = i === active;
            return (
              <button
                key={l.subject}
                onClick={() => setActive(i)}
                className={[
                  'px-5 py-2 rounded-full border-2 font-bold text-sm transition-all duration-200',
                  isActive
                    ? `${c.pill} scale-105 shadow-sm`
                    : 'border-ink/12 text-ink/50 hover:border-ink/25 hover:text-ink/60',
                ].join(' ')}
              >
                {lang === 'en' ? l.tabEn : l.tabNo}
              </button>
            );
          })}
        </motion.div>

        {/* Lesson preview card — animates when subject changes */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className={[
              'relative rounded-3xl border-2 bg-gradient-to-br overflow-hidden shadow-card',
              'p-5 sm:p-8 md:p-10 space-y-6 sm:space-y-7',
              colors.card,
              colors.border,
            ].join(' ')}
          >
            {/* Decorative blob */}
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/40 blur-3xl pointer-events-none" aria-hidden="true" />

            {/* Top row: subject badge + mascot */}
            <div className="flex items-start justify-between relative z-10">
              <div className="space-y-1.5">
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold border-2 ${colors.pill}`}>
                  {lang === 'en' ? lesson.tabEn : lesson.tabNo}
                </span>
                <p className="text-xs font-bold text-ink/50">⏱ {lesson.duration} min</p>
              </div>
              <Mascot subject={lesson.subject} emotion="happy" size={84} />
            </div>

            {/* Movement 1 label + Wonder question */}
            <div className="relative z-10 space-y-4">
              <p className={`text-xs font-extrabold uppercase tracking-widest ${colors.num}`}>
                {t('demo.move1')}
              </p>
              <h3 className="text-2xl md:text-3xl font-black text-ink leading-snug">
                {lang === 'en' ? lesson.hook.en : lesson.hook.no}
              </h3>
              <p className="text-ink/60 text-base font-medium leading-relaxed">
                {lang === 'en' ? lesson.hookBody.en : lesson.hookBody.no}
              </p>
            </div>

            {/* Real-world bridge teaser */}
            <div className="relative z-10 flex items-start gap-3 p-4 rounded-2xl bg-white/60 border border-ink/8">
              <span className="text-lg shrink-0">🌍</span>
              <div>
                <p className="text-xs font-extrabold text-ink/50 uppercase tracking-wider mb-1">
                  {t('demo.bridge')}
                </p>
                <p className="text-sm text-ink/70 font-medium leading-relaxed">
                  {lang === 'en' ? lesson.bridge.en : lesson.bridge.no}
                </p>
              </div>
            </div>

            {/* Movement progression strip — only 01 unlocked */}
            <div className="relative z-10 space-y-2">
              <div className="flex gap-2 flex-wrap">
                {movements.map((mv) => (
                  <div
                    key={mv.num}
                    className={[
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border',
                      mv.locked
                        ? 'border-ink/10 text-ink/50 bg-ink/4'
                        : `border-2 ${colors.pill}`,
                    ].join(' ')}
                  >
                    <span className="font-black">{mv.num}</span>
                    <span>{mv.label}</span>
                    {mv.locked && <span>🔒</span>}
                  </div>
                ))}
              </div>
              <p className="text-xs text-ink/50 font-medium">{t('demo.locked')}</p>
            </div>

            {/* Sign-up CTA */}
            <div className="relative z-10 space-y-2 pt-1">
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(0,194,168,0.30)' }}
                whileTap={{ scale: 0.97 }}
                onClick={onSignup}
                className="w-full py-4 rounded-2xl bg-teal text-white font-extrabold text-base shadow-glow"
              >
                {t('demo.cta')}
              </motion.button>
              <p className="text-center text-xs text-ink/50">{t('demo.nocredit')}</p>
            </div>
          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const ANNUAL_DISCOUNT = 0.15;
function annualMonthly(p: number) { return Math.round(p * (1 - ANNUAL_DISCOUNT)); }

export default function Landing() {
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const isMobile = useIsMobile();

  // Capture UTM params once on mount for first-touch attribution.
  useEffect(() => {
    captureUtm();
  }, []);

  // Wire GSAP micro-interactions (section reveals, hero pulse, magnetic CTAs,
  // navbar scroll behaviour, founder photo parallax, etc.). All loops are
  // disabled under prefers-reduced-motion.
  useLandingMotion();

  // Crawlable internal links to the Undring backlog. The landing page is the
  // highest-authority (and usually indexed) page on the domain, but every other
  // navigation here is an onClick→navigate() button, which crawlers don't
  // follow. These real <Link> anchors funnel crawl signal into the ~100 posts
  // Google has discovered but not yet crawled. We also surface a few posts in
  // the *other* language so the prerendered (Norwegian) landing gives crawlers a
  // path into the English cluster, which is otherwise only reachable via the sitemap.
  const primaryLang: 'no' | 'en' = lang === 'en' ? 'en' : 'no';
  const otherLang: 'no' | 'en' = primaryLang === 'en' ? 'no' : 'en';
  const workshopPosts = getRecentPosts(primaryLang, 9);
  const workshopPostsOtherLang = getRecentPosts(otherLang, 6);

  const moves = [
    {
      num: '01', title: t('move1.title'), sub: t('move1.sub'), desc: t('move1.desc'),
      bg: '#FEF3C7', border: '#FDE68A', text: '#92400E', numColor: '#F59E0B',
    },
    {
      num: '02', title: t('move2.title'), sub: t('move2.sub'), desc: t('move2.desc'),
      bg: '#DCFCE7', border: '#86EFAC', text: '#14532D', numColor: '#16A34A',
    },
    {
      num: '03', title: t('move3.title'), sub: t('move3.sub'), desc: t('move3.desc'),
      bg: '#DBEAFE', border: '#93C5FD', text: '#1E3A8A', numColor: '#2563EB',
    },
    {
      num: '04', title: t('move4.title'), sub: t('move4.sub'), desc: t('move4.desc'),
      bg: '#F3E8FF', border: '#D8B4FE', text: '#4C1D95', numColor: '#9333EA',
    },
  ];

  return (
    <div className="min-h-screen bg-page text-ink font-display overflow-x-hidden">
      <SEOHead meta={{ title: lang === 'en' ? 'Learning that starts with wonder' : 'Læring som starter med undring', description: lang === 'en' ? 'Good Atoms — STEAM lessons for Nordic children aged 4–16, built on LK20 and developed in Verkstedet, our editorial workshop.' : 'Good Atoms — STEAM-leksjoner for nordiske barn 4–16 år, bygget på LK20 og utviklet i Verkstedet, vårt redaksjonelle lag.', url: 'https://goodatoms.com/', locale: lang === 'en' ? 'en' : 'nb_NO' }} />

      {/* ── NAV ─────────────────────────────────────────────────── */}
      <nav data-anima-navbar className="sticky top-0 z-50 bg-page/95 backdrop-blur-md border-b border-ink/8 transition-shadow duration-300" aria-label={lang === 'en' ? 'Main navigation' : 'Hovednavigasjon'}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <motion.span
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...spring, delay: 0.1 }}
            className="good-atoms-wordmark text-teal text-base sm:text-xl tracking-[0.12em] sm:tracking-[0.2em] cursor-pointer shrink-0"
            onClick={() => navigate('/')}
          >
            Good Atoms
          </motion.span>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...spring, delay: 0.15 }}
            className="flex items-center gap-2 sm:gap-4"
          >
            <button
              data-anima-nav-link
              onClick={() => navigate('/undring')}
              className="text-ink/60 hover:text-teal text-sm font-semibold transition-colors hidden sm:block"
            >
              {lang === 'en' ? 'Wonder' : 'Undring'}
            </button>
            <button
              data-anima-nav-link
              onClick={() => navigate('/verkstedet')}
              className="text-ink/60 hover:text-teal text-sm font-semibold transition-colors hidden sm:block"
            >
              {/* Norwegian-first; English mirrors with "Studio" so the meaning is obvious to non-Norwegians while keeping the brand term front of mind in NO. */}
              {lang === 'en' ? 'Studio' : 'Verkstedet'}
            </button>
            <LanguageToggle compact />
            <button
              onClick={() => navigate('/login')}
              className="text-ink/50 hover:text-ink text-sm font-semibold transition-colors hidden sm:block"
            >
              {t('nav.login')}
            </button>
            <Button onClick={makeCtaHandler(navigate, 'kom_i_gang', 'navbar')} size="sm">
              {t('nav.get_started')}
            </Button>
          </motion.div>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative min-h-[90vh] sm:min-h-[95vh] flex flex-col items-center justify-center px-4 sm:px-6 py-12 sm:py-20 overflow-hidden">

        {/* Background color blobs */}
        <FloatBlob size={260} color="#FDE68A" x="2%"  y="5%"  delay={0}   duration={5.5} />
        <FloatBlob size={180} color="#6EE7B7" x="82%" y="6%"  delay={0.8} duration={6.5} />
        <FloatBlob size={140} color="#93C5FD" x="76%" y="68%" delay={1.4} duration={4.5} />
        <FloatBlob size={200} color="#FCA5A5" x="5%"  y="65%" delay={0.5} duration={7}   />
        <FloatBlob size={100} color="#C4B5FD" x="48%" y="88%" delay={1.2} duration={5}   />

        {/* Desktop: floating mascots at corners — hidden on small screens */}
        <motion.div
          aria-hidden="true"
          className="absolute top-[10%] left-[4%] hidden md:block"
          initial={{ x: -80, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          transition={{ ...spring, delay: 0.3 }}
        >
          <Mascot subject="mathematics" size={96} emotion="happy" />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute top-[52%] left-[2%] hidden md:block"
          initial={{ x: -60, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          transition={{ ...spring, delay: 0.5 }}
        >
          <Mascot subject="science" size={76} emotion="happy" />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute top-[8%] right-[4%] hidden md:block"
          initial={{ x: 80, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          transition={{ ...spring, delay: 0.4 }}
        >
          <Mascot subject="technology" size={88} emotion="happy" />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute top-[55%] right-[3%] hidden md:block"
          initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          transition={{ ...spring, delay: 0.6 }}
        >
          <Mascot subject="engineering" size={72} emotion="happy" />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute bottom-[8%] right-[28%] hidden md:block"
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          transition={{ ...spring, delay: 0.7 }}
        >
          <Mascot subject="arts" size={80} emotion="happy" />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute bottom-[8%] left-[28%] hidden md:block"
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          transition={{ ...spring, delay: 0.8 }}
        >
          <Mascot subject="nature" size={84} emotion="happy" />
        </motion.div>

        {/* Mobile: mascot row at top */}
        <motion.div
          aria-hidden="true"
          className="flex md:hidden items-end gap-2 sm:gap-3 mb-6 sm:mb-8"
          variants={stagger} initial="hidden" animate="show"
        >
          {SUBJECTS.map((s) => (
            <motion.div key={s.subject} variants={popIn}>
              <Mascot subject={s.subject} size={40} emotion="happy" />
            </motion.div>
          ))}
        </motion.div>

        {/* Center content */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="relative z-10 max-w-3xl mx-auto text-center space-y-6 sm:space-y-8 w-full"
        >
          <motion.div variants={fadeUp} className="space-y-3">
            <p className="text-ink/50 text-xs sm:text-sm font-extrabold uppercase tracking-[0.15em] sm:tracking-[0.25em]">
              {lang === 'en' ? '15 minutes that ignite wonder' : '15 minutter som tenner undring'}
            </p>
            <h1 className="font-black tracking-tight leading-none">
              <span className="text-2xl sm:text-4xl md:text-6xl block mb-1 leading-tight">
                {t('hero.title_prefix')}
              </span>
              <span className="good-atoms-wordmark text-teal text-[2.5rem] sm:text-6xl md:text-8xl relative inline-block">
                {/* On mobile, render plain text to avoid per-character overflow;
                    on desktop, use the interactive VariableProximity effect */}
                {isMobile ? (
                  'GOOD ATOMS'
                ) : (
                  <VariableProximity
                    label="Good Atoms"
                    minWeight={700}
                    maxWeight={900}
                    radius={120}
                    falloff={1.8}
                  />
                )}
                {/* Underline squiggle accent */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ ...spring, delay: 0.9 }}
                  style={{ transformOrigin: 'left center' }}
                  className="absolute -bottom-1 sm:-bottom-2 left-0 right-0 h-1.5 sm:h-2 bg-teal/20 rounded-full"
                />
              </span>
            </h1>
          </motion.div>

          <motion.p
            variants={fadeUp}
            className="text-base sm:text-lg md:text-xl text-ink/60 max-w-xl mx-auto leading-relaxed font-medium px-2 sm:px-0"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full px-2 sm:px-0"
          >
            <motion.div data-anima-hero-cta data-anima-press whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
              <Button onClick={makeCtaHandler(navigate, 'start_gratis', 'hero')} size={isMobile ? 'lg' : 'xl'} fullWidth={isMobile}>
                {t('hero.cta1')}
              </Button>
            </motion.div>
            <motion.button
              data-anima-press
              whileHover={{ scale: 1.03, backgroundColor: 'rgba(30,30,46,0.06)' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-2xl border-2 border-ink/15 text-ink font-bold text-sm sm:text-base transition-all"
            >
              {t('hero.cta2')} →
            </motion.button>
          </motion.div>

          <motion.p variants={fadeUp} className="text-ink/50 text-xs sm:text-sm px-2 sm:px-0">
            {t('hero.disclaimer')}
          </motion.p>
        </motion.div>
      </section>

      {/* ── LIVE DEMO (moved up: parents need "what will my kid do" before philosophy) ── */}
      <LessonDemoSection lang={lang} t={t} onSignup={makeCtaHandler(navigate, 'lesson_preview', 'demo')} />

      {/* ── SAGAN QUOTE ──────────────────────────────────────────── */}
      <section className="bg-teal py-10 sm:py-12 px-4 sm:px-6 overflow-hidden relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={spring}
          className="max-w-2xl mx-auto text-center relative z-10"
        >
          <div className="text-white/30 text-6xl font-serif leading-none mb-1">"</div>
          <blockquote className="text-base sm:text-lg md:text-2xl text-white font-bold italic leading-relaxed">
            {t('sagan.quote')}
          </blockquote>
          <cite className="block mt-4 text-white/60 text-xs not-italic font-semibold tracking-wider uppercase">
            — Carl Sagan
          </cite>
        </motion.div>
      </section>

      {/* ── SAGAN VIDEO (reduced visual weight, pushed below the lesson preview) ── */}
      <section className="py-10 sm:py-14 px-4 sm:px-6 bg-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={springLazy}
          className="max-w-2xl mx-auto"
        >
          <p className="text-center text-ink/50 text-xs font-extrabold uppercase tracking-[0.25em] mb-2">
            {lang === 'en' ? 'Why it matters' : 'Hvorfor det betyr noe'}
          </p>
          <h2 className="text-center text-base sm:text-lg md:text-xl font-black text-ink mb-5 leading-snug px-2 sm:px-0">
            {lang === 'en'
              ? '"Every kid starts out as a natural-born scientist."'
              : '"Hvert barn starter som en naturlig vitenskapsmann."'}
          </h2>

          {/* Compact video wrapper */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden border border-ink/8 shadow-card bg-ink">
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube-nocookie.com/embed/acBRahW5c-A"
                  title="Carl Sagan"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>

          <p className="text-center text-ink/50 text-xs mt-3 font-medium">
            {lang === 'en'
              ? 'Carl Sagan on kids, questions, and the educational system — the problem Good Atoms was built to solve.'
              : 'Carl Sagan om barn, spørsmål og skolesystemet — problemet Good Atoms ble bygget for å løse.'}
          </p>
        </motion.div>
      </section>

      {/* ── THE FOUR MOVEMENTS ───────────────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-page">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={spring}
            className="text-center mb-14"
          >
            <p className="text-ink/50 text-xs font-extrabold uppercase tracking-[0.25em] mb-3">
              {lang === 'en' ? 'The method' : 'Metoden'}
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-ink">
              {t('movements.heading')}
            </h2>
            <p className="text-ink/50 text-base sm:text-lg mt-4 max-w-2xl mx-auto font-medium px-2 sm:px-0">
              {t('movements.sub')}
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {moves.map((step) => (
              <motion.div
                key={step.num}
                variants={fadeUp}
                whileHover={{ y: -6, boxShadow: '0 12px 40px rgba(30,30,46,0.12)' }}
                className="rounded-3xl p-7 space-y-4 border-2 transition-shadow"
                style={{ background: step.bg, borderColor: step.border }}
              >
                <div
                  className="text-5xl font-black leading-none"
                  style={{ color: step.numColor }}
                >
                  {step.num}
                </div>
                <div>
                  <div
                    className="text-xs font-extrabold tracking-widest uppercase mb-1"
                    style={{ color: step.numColor }}
                  >
                    {step.sub}
                  </div>
                  <h3 className="text-xl font-black" style={{ color: step.text }}>{step.title}</h3>
                </div>
                <p className="text-sm leading-relaxed font-medium" style={{ color: step.text + 'bb' }}>
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── MEET THE CREW ────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 overflow-hidden" style={{ background: '#FFF8E7' }}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={spring}
            className="text-center mb-14"
          >
            <p className="text-ink/50 text-xs font-extrabold uppercase tracking-[0.25em] mb-3">
              {lang === 'en' ? '6 subjects · infinite curiosity' : '6 fag · uendelig nysgjerrighet'}
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-ink">
              {t('subjects.heading')}
            </h2>
            <p className="text-ink/50 text-base sm:text-lg mt-4 font-medium">{t('subjects.sub')}</p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-40px' }}
            className="grid grid-cols-3 sm:flex sm:flex-wrap justify-center gap-6 sm:gap-10"
          >
            {SUBJECTS.map((s) => (
              <motion.div
                key={s.subject}
                variants={popIn}
                whileHover={{ y: -8, scale: 1.05 }}
                className="flex flex-col items-center gap-3 cursor-default"
              >
                <Mascot subject={s.subject} size={100} emotion="happy" />
                <div className="text-center">
                  <div className="font-extrabold text-ink text-base">
                    {MASCOT_NAMES[s.subject]}
                  </div>
                  <div className="text-ink/50 text-sm font-semibold">
                    {lang === 'en' ? s.labelEn : s.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── STATS BAND ───────────────────────────────────────────── */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 bg-ink overflow-hidden">
        <div className="max-w-5xl mx-auto space-y-10">

          {/* ── Tier 1: Value statement — full width, hero weight ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={spring}
            className="text-center space-y-3"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl bg-teal/10 border border-teal/20">
              <span className="text-xl">🚫</span>
              <span className="text-2xl md:text-3xl font-black text-chalk tracking-tight">
                {lang === 'en' ? 'No ads. Ever.' : 'Ingen reklame. Aldri.'}
              </span>
            </div>
            <p className="text-chalk/60 text-sm font-medium max-w-sm mx-auto leading-relaxed">
              {lang === 'en'
                ? 'Pure curiosity — no distractions, no tracking, no upsells to kids.'
                : 'Ren nysgjerrighet — ingen distraksjoner, ingen sporing, ingen reklame til barn.'}
            </p>
          </motion.div>

          {/* ── Tier 2: Product depth + curriculum depth — medium weight ── */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-4"
          >
            {[
              {
                number: '15',
                icon: '🎮',
                label: lang === 'en' ? 'interactive games' : 'interaktive spill',
                sub: lang === 'en' ? 'Product depth' : 'Produktdybde',
              },
              {
                number: '4',
                icon: '📈',
                label: lang === 'en' ? 'learning tiers' : 'læringstrinn',
                sub: lang === 'en' ? 'Curriculum depth' : 'Pensumstruktur',
              },
            ].map((s) => (
              <motion.div
                key={s.label}
                variants={popIn}
                className="rounded-2xl bg-white/5 border border-white/8 p-5 md:p-7 space-y-2"
              >
                <span className="text-2xl">{s.icon}</span>
                <div className="flex items-baseline gap-2.5 flex-wrap">
                  <span className="text-5xl md:text-6xl font-black text-teal leading-none">{s.number}</span>
                  <span className="text-chalk/80 font-bold text-sm md:text-base">{s.label}</span>
                </div>
                <span className="block text-chalk/50 text-xs uppercase tracking-widest font-bold">{s.sub}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* ── Tier 3: Supporting credentials — small, muted ── */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-3 gap-4 pt-6 border-t border-white/8"
          >
            {/* LK20 — curriculum alignment */}
            <motion.div variants={popIn} className="flex flex-col items-center gap-1.5 text-center">
              <span className="text-2xl md:text-3xl font-black text-teal leading-none tracking-tight">LK20</span>
              <span className="text-chalk/60 font-bold text-xs uppercase tracking-widest">
                {lang === 'en' ? 'Norwegian curriculum' : 'Kunnskapsløftet'}
              </span>
            </motion.div>

            {/* Age range */}
            <motion.div variants={popIn} className="flex flex-col items-center gap-1.5 text-center">
              <span className="text-3xl md:text-4xl font-black text-chalk/70 leading-none">4–16</span>
              <span className="text-chalk/60 font-bold text-xs uppercase tracking-widest">
                {lang === 'en' ? 'years old' : 'år'}
              </span>
            </motion.div>

            {/* Made in Norway */}
            <motion.div variants={popIn} className="flex flex-col items-center gap-1.5 text-center">
              <span className="text-3xl md:text-4xl font-black text-chalk/70 leading-none">🇳🇴</span>
              <span className="text-chalk/60 font-bold text-xs uppercase tracking-widest">
                {lang === 'en' ? 'Made in Norway' : 'Laget i Norge'}
              </span>
            </motion.div>
          </motion.div>

          {/* ── Tier 4: Micro curriculum note — whisper weight ── */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center text-chalk/50 text-xs font-medium pt-2"
          >
            {lang === 'en'
              ? 'Lessons mapped to LK20 · NDLA · CS Unplugged · Code.org · Scratch Foundation'
              : 'Leksjoner tilpasset LK20 · NDLA · CS Unplugged · Code.org · Scratch Foundation'}
          </motion.p>

        </div>
      </section>

      {/* ── HYBRID LEARNING ──────────────────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-page">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springLazy}
            className="space-y-6"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal/10 border-2 border-teal/20 text-teal text-xs font-extrabold uppercase tracking-widest">
              {t('hybrid.tag')}
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight whitespace-pre-line text-ink">
              {t('hybrid.heading')}
            </h2>
            <p className="text-ink/60 text-lg leading-relaxed font-medium">
              {t('hybrid.body')}
            </p>
            <ul className="space-y-3">
              {[t('hybrid.feat1'), t('hybrid.feat2'), t('hybrid.feat3'), t('hybrid.feat4')].map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...spring, delay: i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <span className="w-6 h-6 rounded-full bg-teal/15 border-2 border-teal/30 flex items-center justify-center text-teal text-xs font-extrabold shrink-0 mt-0.5">
                    ✓
                  </span>
                  <span className="text-ink/70 font-medium">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right: worksheet mock */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springLazy}
            className="relative"
          >
            {/* Drop shadow card behind */}
            <div
              className="absolute inset-0 rounded-3xl translate-x-3 translate-y-3"
              style={{ background: '#FDE68A' }}
            />
            <div className="relative rounded-3xl p-8 shadow-card text-graphite space-y-4 border-2 border-ink/8"
              style={{ background: '#FFFFFF' }}>
              <div className="flex items-center justify-between">
                <span className="good-atoms-wordmark text-navy text-sm tracking-[0.15em]">Good Atoms</span>
                <span className="text-xs text-graphite/50">
                  {lang === 'en' ? 'Mathematics · 8–12 yrs' : 'Matematikk · 8–12 år'}
                </span>
              </div>
              <h3 className="font-body text-xl font-bold text-navy">
                {lang === 'en' ? 'Fraction Exploration' : 'Brøk-utforskning'}
              </h3>
              <div className="border-t border-graphite/10 pt-4 space-y-2">
                {[lang === 'en' ? 'Name:' : 'Navn:', lang === 'en' ? 'Date:' : 'Dato:'].map((label) => (
                  <div key={label} className="flex gap-4 text-sm">
                    <span className="text-graphite/50 w-10">{label}</span>
                    <div className="flex-1 border-b border-graphite/30" />
                  </div>
                ))}
              </div>
              <div className="space-y-2 pt-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-graphite/40">
                  {lang === 'en' ? 'Warm-up' : 'Oppvarming'}
                </div>
                {(lang === 'en'
                  ? ['1. Which fraction is shaded? ___', '2. Write the fraction in words: 3/4 = ___', '3. Draw 1/2 of a circle:']
                  : ['1. Hvilken brøk er skyggelagt? ___', '2. Skriv brøken som ord: 3/4 = ___', '3. Tegn 1/2 av en sirkel:']
                ).map((q) => (
                  <div key={q} className="text-sm text-graphite/70 py-2 border-b border-graphite/10">{q}</div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CREDIBILITY: what we build on ────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 overflow-hidden" style={{ background: '#F0FDF4' }}>
        <div className="max-w-5xl mx-auto space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={spring}
            className="text-center"
          >
            <p className="text-xs font-extrabold text-teal uppercase tracking-[0.25em] mb-3">
              {lang === 'en' ? 'Curriculum + craft' : 'Pensum + håndverk'}
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-ink">
              {lang === 'en' ? 'What we build on' : 'Hva vi bygger på'}
            </h2>
            <p className="text-ink/55 text-base sm:text-lg mt-3 max-w-2xl mx-auto font-medium">
              {lang === 'en'
                ? 'Concrete proof of curriculum alignment — no fabricated quotes, just the goals we map every lesson to.'
                : 'Konkret bevis på pensumtilpasning — ingen oppdiktede sitater, bare målene vi knytter hver leksjon til.'}
            </p>
          </motion.div>

          {/* (a) LK20 alignment card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={spring}
            className="rounded-3xl bg-white border-2 border-ink/8 shadow-card overflow-hidden"
          >
            <div className="px-6 sm:px-8 py-5 border-b border-ink/8 bg-white flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs font-extrabold text-teal uppercase tracking-widest">
                  {lang === 'en' ? 'LK20 alignment' : 'LK20-tilpasning'}
                </p>
                <p className="font-display font-black text-ink text-lg mt-0.5">
                  {lang === 'en'
                    ? 'Three lessons, three competence goals'
                    : 'Tre leksjoner, tre kompetansemål'}
                </p>
              </div>
              <span className="text-xs font-bold text-ink/40 uppercase tracking-wider">
                {lang === 'en' ? 'Source: Utdanningsdirektoratet' : 'Kilde: Utdanningsdirektoratet'}
              </span>
            </div>
            <div className="divide-y divide-ink/6">
              {LK20_ALIGNMENT.map((row) => (
                <div key={row.titleNo} className="px-6 sm:px-8 py-5 grid md:grid-cols-12 gap-3 md:gap-6 items-start">
                  <div className="md:col-span-4">
                    <p className="font-display font-bold text-ink text-base leading-snug">
                      {lang === 'en' ? row.titleEn : row.titleNo}
                    </p>
                    <p className="font-display text-xs text-ink/50 mt-1">
                      {lang === 'en' ? row.gradeEn : row.gradeNo}
                    </p>
                  </div>
                  <p className="md:col-span-8 font-body text-ink/70 text-sm sm:text-base leading-relaxed">
                    {lang === 'en' ? row.goalEn : row.goalNo}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* (b) Pedagogical sources row */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...spring, delay: 0.1 }}
            className="text-center space-y-4"
          >
            <p className="text-xs font-extrabold text-ink/50 uppercase tracking-[0.2em]">
              {lang === 'en' ? 'Pedagogical frameworks we draw from' : 'Pedagogiske rammeverk vi bygger på'}
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 max-w-3xl mx-auto">
              {PEDAGOGICAL_SOURCES.map((source, i) => (
                <li key={source} className="flex items-center gap-5">
                  <span className="font-display font-bold text-ink/70 text-sm sm:text-base tracking-wide">
                    {source}
                  </span>
                  {i < PEDAGOGICAL_SOURCES.length - 1 && (
                    <span aria-hidden="true" className="text-ink/25">·</span>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* (c) Live lesson preview tile — Blobby + rainbow */}
          <motion.div
            data-anima-lift
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={spring}
            className="rounded-3xl bg-white border-2 border-ink/8 shadow-card overflow-hidden grid md:grid-cols-5"
          >
            {/* Lesson illustration — Blobby (science mascot) + rainbow */}
            <div className="md:col-span-2 relative bg-gradient-to-br from-[#F5EDD8] via-[#FAFAF8] to-[#E0F7F2] aspect-[4/3] md:aspect-auto flex items-center justify-center p-6">
              <LessonPreviewIllustration
                className="w-full h-full max-w-[320px]"
                ariaLabel={lang === 'en'
                  ? 'Blobby the science mascot looks up at a rainbow, holding a raindrop with light bending through it'
                  : 'Blobby naturfag-maskoten ser opp på en regnbue og holder en regndråpe som bøyer lyset'}
              />
            </div>

            {/* Lesson preview text */}
            <div className="md:col-span-3 p-6 sm:p-8 md:p-10 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold border-2 bg-[#DCFCE7] text-[#16A34A] border-[#BBF7D0]">
                  {lang === 'en' ? 'Science' : 'Naturfag'}
                </span>
                <span className="text-xs font-bold text-ink/50">
                  {lang === 'en' ? '5–11 yrs · 20 min' : '5–11 år · 20 min'}
                </span>
              </div>
              <p className="text-xs font-extrabold text-teal uppercase tracking-widest">
                {lang === 'en' ? 'What an actual Good Atoms lesson looks like' : 'Slik ser en ekte Good Atoms-leksjon ut'}
              </p>
              <h3 className="font-display font-black text-ink text-xl sm:text-2xl leading-snug">
                {lang === 'en'
                  ? 'What is a rainbow, really? How sun and rain together make a whole spectrum of colour.'
                  : 'Hva er en regnbue? Slik lager sol og regn et helt fargespill.'}
              </h3>
              <p className="font-body text-ink/70 text-base leading-relaxed">
                {lang === 'en'
                  ? 'A rainbow is sunlight that has hidden inside a raindrop. The drop bends the light and throws it back out as seven colours — straight to your eyes.'
                  : 'En regnbue er sollys som har gjemt seg inni regndråper. Slik bøyer dråpen lyset og kaster det ut igjen som syv farger — rett til øynene dine.'}
              </p>
              <div className="pt-2">
                <button
                  data-anima-nudge-arrow
                  data-anima-press
                  onClick={() => navigate('/undring/hva-er-en-regnbue-barn')}
                  className="inline-flex items-center gap-2 font-display font-bold text-sm text-teal hover:text-teal/80 transition-colors"
                >
                  {lang === 'en' ? 'Read the full lesson' : 'Les hele leksjonen'} <span className="arrow">→</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOUNDER CARD ─────────────────────────────────────────── */}
      <FounderCard lang={lang === 'en' ? 'en' : 'no'} />

      {/* ── PRICING ──────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-page">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={spring}
            className="text-center mb-10 sm:mb-14"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-ink mb-4">{t('pricing.heading')}</h2>
            <p className="text-ink/50 text-base sm:text-lg font-medium">{t('pricing.sub')}</p>
          </motion.div>

          {/* ── Billing toggle ── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 mb-10"
          >
            <div className="flex items-center bg-ink/6 rounded-full p-1 gap-1">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                  billing === 'monthly' ? 'bg-white text-ink shadow-sm' : 'text-ink/50 hover:text-ink/60'
                }`}
              >
                {lang === 'no' ? 'Månedlig' : 'Monthly'}
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                  billing === 'annual' ? 'bg-white text-ink shadow-sm' : 'text-ink/50 hover:text-ink/60'
                }`}
              >
                {lang === 'no' ? 'Årsvis' : 'Annual'}
                <span className="bg-teal text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full">
                  −15%
                </span>
              </button>
            </div>
            <AnimatePresence>
              {billing === 'annual' && (
                <motion.span
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  className="text-xs text-teal font-semibold hidden sm:block"
                >
                  {lang === 'no' ? 'Fakturert én gang per år' : 'Billed once per year'}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6"
          >
            {[
              {
                name: t('plan.premium.name'), monthlyPrice: 99, period: t('plan.premium.period'),
                features: [t('plan.premium.feat1'), t('plan.premium.feat2'), t('plan.premium.feat3'), t('plan.premium.feat4'), t('plan.premium.feat5')],
                highlight: false, bg: '#FFFFFF', border: '#E2E8F0',
              },
              {
                name: t('plan.home.name'), monthlyPrice: 149, period: t('plan.home.period'),
                features: [t('plan.home.feat1'), t('plan.home.feat2'), t('plan.home.feat3'), t('plan.home.feat4')],
                highlight: true, bg: '#00C2A8', border: '#009E89',
              },
              {
                name: t('plan.family.name'), monthlyPrice: 189, period: t('plan.family.period'),
                features: [t('plan.family.feat1'), t('plan.family.feat2'), t('plan.family.feat3'), t('plan.family.feat4'), t('plan.family.feat5')],
                highlight: false, bg: '#FFFFFF', border: '#E2E8F0',
              },
            ].map((plan) => {
              const isAnnual     = billing === 'annual';
              const displayPrice = isAnnual ? annualMonthly(plan.monthlyPrice) : plan.monthlyPrice;
              const annualTotal  = isAnnual ? annualMonthly(plan.monthlyPrice) * 12 : null;
              const annualSave   = isAnnual ? plan.monthlyPrice * 12 - (annualMonthly(plan.monthlyPrice) * 12) : null;
              const ctaLabel     = isAnnual
                ? (lang === 'no' ? `Start for ${annualMonthly(plan.monthlyPrice) * 12} kr/år` : `Start for ${annualMonthly(plan.monthlyPrice) * 12} kr/yr`)
                : (lang === 'no' ? `Start for ${plan.monthlyPrice} kr` : `Start for ${plan.monthlyPrice} kr`);

              return (
              <motion.div
                key={plan.name}
                variants={popIn}
                {...(plan.highlight
                  ? { 'data-anima-pricing-highlight': '' }
                  : { 'data-anima-pricing-card': '' })}
                className="rounded-3xl p-7 space-y-6 border-2 shadow-card"
                style={{ background: plan.bg, borderColor: plan.border }}
              >
                <div>
                  <div className="text-sm font-extrabold mb-3" style={{ color: plan.highlight ? 'rgba(255,255,255,0.7)' : '#94A3B8' }}>
                    {plan.name}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={displayPrice}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.15 }}
                        className="text-5xl font-black"
                        style={{ color: plan.highlight ? '#FFFFFF' : '#1E1E2E' }}
                      >
                        {displayPrice}
                      </motion.span>
                    </AnimatePresence>
                    <span className="text-sm font-semibold" style={{ color: plan.highlight ? 'rgba(255,255,255,0.5)' : '#94A3B8' }}>
                      {plan.period}
                    </span>
                  </div>
                  <AnimatePresence>
                    {annualTotal && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-xs mt-1"
                        style={{ color: plan.highlight ? 'rgba(255,255,255,0.5)' : '#94A3B8' }}
                      >
                        {lang === 'no' ? `Fakturert ${annualTotal.toLocaleString('nb-NO')} kr/år` : `Billed ${annualTotal} kr/yr`}
                      </motion.p>
                    )}
                  </AnimatePresence>
                  <AnimatePresence>
                    {annualSave && annualSave > 0 && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="inline-block mt-1.5 text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{
                          background: plan.highlight ? 'rgba(255,255,255,0.2)' : '#CCFBF1',
                          color: plan.highlight ? '#FFFFFF' : '#0D9488',
                        }}
                      >
                        {lang === 'no' ? `Spar ${annualSave} kr/år` : `Save ${annualSave} kr/yr`}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>
                <ul className="space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm font-semibold"
                      style={{ color: plan.highlight ? 'rgba(255,255,255,0.85)' : '#475569' }}>
                      <span style={{ color: plan.highlight ? '#FFFFFF' : '#00C2A8' }} className="font-extrabold">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <motion.button
                  data-anima-magnetic
                  data-anima-press
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={makeCtaHandler(navigate, `pricing_${plan.name.toLowerCase().replace(/\s+/g, '_')}`, 'pricing')}
                  className="w-full py-3.5 rounded-2xl font-extrabold text-sm transition-all"
                  style={{
                    background: plan.highlight ? '#1E1E2E' : '#F8FAFC',
                    color: plan.highlight ? '#FFFFFF' : '#1E1E2E',
                    border: plan.highlight ? 'none' : '2px solid #E2E8F0',
                  }}
                >
                  {ctaLabel}
                </motion.button>
              </motion.div>
            )})}
          </motion.div>

          <p className="text-center text-ink/50 text-sm mt-8 font-medium">
            {t('plan.free.note')}
          </p>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-ink relative overflow-hidden">
        {/* Subtle blobs on dark section */}
        <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full bg-teal/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full bg-teal/8 blur-3xl pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={spring}
          className="max-w-3xl mx-auto text-center space-y-8 relative z-10"
        >
          {/* Crew — each character with their own posture and emotion */}
          {(() => {
            const poses: { emotion: MascotEmotion; rotate: number; y: number; size: number }[] = [
              { emotion: 'happy',       rotate: -8,  y: 6,   size: 72 },
              { emotion: 'thinking',    rotate: 6,   y: -10, size: 82 },
              { emotion: 'happy',       rotate: -4,  y: 4,   size: 76 },
              { emotion: 'surprised',   rotate: 9,   y: -6,  size: 70 },
              { emotion: 'happy',       rotate: -6,  y: 8,   size: 78 },
              { emotion: 'idle',        rotate: 5,   y: -4,  size: 74 },
            ]
            return (
              <div className="flex justify-center items-end gap-2 sm:gap-4 mb-4 flex-wrap">
                {SUBJECTS.map((s, i) => {
                  const p = poses[i]
                  return (
                    <motion.div
                      key={s.subject}
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: p.y }}
                      viewport={{ once: true }}
                      transition={{ type: 'spring' as const, stiffness: 220, damping: 20, delay: i * 0.09 }}
                      style={{ rotate: p.rotate }}
                    >
                      <Mascot subject={s.subject} emotion={p.emotion} size={p.size} />
                    </motion.div>
                  )
                })}
              </div>
            )
          })()}

          <h2 className="text-3xl sm:text-4xl md:text-6xl font-black text-chalk leading-tight">
            {t('cta.heading')}
          </h2>
          <p className="text-chalk/80 text-base sm:text-lg font-medium max-w-xl mx-auto px-2 sm:px-0">
            {t('cta.body')}
          </p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            <Button onClick={makeCtaHandler(navigate, 'final_cta', 'footer')} size="xl">
              {t('cta.btn')}
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* ── FROM THE WORKSHOP (crawlable links into the Undring backlog) ── */}
      {workshopPosts.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 bg-page border-t border-ink/8">
          <div className="max-w-6xl mx-auto space-y-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={spring}
              className="text-center space-y-3"
            >
              <p className="text-xs font-extrabold text-teal uppercase tracking-[0.25em]">
                {primaryLang === 'en' ? 'From the workshop' : 'Fra Verkstedet'}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-ink">
                {primaryLang === 'en' ? 'Wonder, freshly written' : 'Undring, ferskt skrevet'}
              </h2>
              <p className="text-ink/50 text-base sm:text-lg font-medium max-w-2xl mx-auto">
                {primaryLang === 'en'
                  ? 'Articles on STEAM learning, science, creativity and digital parenting — with human review before publication.'
                  : 'Artikler om STEAM-læring, realfag, kreativitet og digitalt foreldreskap — med menneskelig fagsjekk før publisering.'}
              </p>
            </motion.div>

            {/* Recent posts in the active language — real <Link> anchors */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workshopPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>

            {/* Cross-language discovery row — gives crawlers a path into the
                other-language post cluster from this high-authority page. */}
            {workshopPostsOtherLang.length > 0 && (
              <div className="pt-2 text-center space-y-3">
                <p className="text-xs font-extrabold text-ink/40 uppercase tracking-[0.2em]">
                  {/* Label stays in the page's own language; it points readers
                      (and crawlers) at the other-language articles. */}
                  {primaryLang === 'en' ? 'Also in Norwegian' : 'Også på engelsk'}
                </p>
                <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 max-w-3xl mx-auto">
                  {workshopPostsOtherLang.map((post) => (
                    <li key={post.slug}>
                      <Link
                        to={`/undring/${post.slug}`}
                        hrefLang={otherLang}
                        className="text-sm font-semibold text-ink/60 hover:text-teal underline-offset-4 hover:underline transition-colors"
                      >
                        {post.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-center">
              <Link
                to="/undring"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl border-2 border-ink/15 text-ink font-bold text-sm hover:border-teal hover:text-teal transition-all"
              >
                {primaryLang === 'en' ? 'Read all articles' : 'Les alle artikler'} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── FOOTER ───────────────────────────────────────────────── */}
      <footer className="bg-page border-t border-ink/8 py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="good-atoms-wordmark text-teal tracking-[0.2em]">Good Atoms</span>
            <p className="text-ink/50 text-sm">
              &copy; {new Date().getFullYear()} Good Atoms AS, Oslo, Norway
            </p>
            <div className="flex gap-6 text-ink/50 text-sm">
              <button onClick={() => navigate('/verkstedet')} className="hover:text-ink/60 transition-colors">
                {lang === 'en' ? 'Studio' : 'Verkstedet'}
              </button>
              <button onClick={() => navigate('/privacy')} className="hover:text-ink/60 transition-colors">{t('footer.privacy')}</button>
              <button onClick={() => navigate('/terms')} className="hover:text-ink/60 transition-colors">{t('footer.terms')}</button>
            </div>
          </div>
          <p className="text-center text-ink/40 text-xs max-w-2xl mx-auto">
            {lang === 'en' ? 'Content developed in ' : 'Innhold utviklet i '}
            <button onClick={() => navigate('/verkstedet')} className="underline hover:text-ink/60">
              Verkstedet
            </button>
            {lang === 'en'
              ? ' — our editorial workshop — with human review before publication.'
              : ' — vårt redaksjonelle lag — med menneskelig fagsjekk før publisering.'}
          </p>
        </div>
      </footer>

    </div>
  );
}
